import { Cancel, CheckCircle, ContentCopy, Delete, InfoOutline } from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Tooltip
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { getLanguage } from 'data/locale'
import backend from 'utils/backend'
import { getLongFormatDateTime } from 'utils/dates'
import { renderRichText } from 'utils/helpers'

const MAX_ACTIVE_TOKENS = 3

const UserManageTokens = ({ userData }) => {
  const { t } = getLanguage()

  const [tokenSecrets, setTokenSecrets] = useState({})

  const initialCreateDialogState = {
    open: false,
    tokenName: '',
    tokenNameError: null,
    requestInFlight: false,
    serverError: null
  }
  const [createDialogState, setCreateDialogState] = useState(initialCreateDialogState)

  const initialDeleteDialogState = {
    open: false,
    tokenId: null,
    requestInFlight: false,
    serverError: null
  }
  const [deleteDialogState, setDeleteDialogState] = useState(initialDeleteDialogState)

  const initialCopiedState = {
    tokenId: null,
    success: null
  }
  const [copiedState, setCopiedState] = useState(initialCopiedState)
  const copiedTimeoutRef = useRef(null)

  const queryClient = useQueryClient()
  const { data: tokens, isLoading } = useQuery({
    queryKey: ['userTokens'],
    queryFn: () => backend.get('api/token/pat/').then(res => res.data.results)
  })

  const activeTokens = useMemo(
    () =>
      tokens
        ? tokens.filter(token => !token.revokedAt && new Date(token.expiresAt) > new Date())
        : [],
    [tokens]
  )

  const getTokenSecret = tokenId => `${tokenId}_${tokenSecrets[tokenId]}`

  const copyTokenSecret = tokenId => async () => {
    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current)
    }

    let success = true
    try {
      await navigator.clipboard.writeText(getTokenSecret(tokenId))
    } catch (err) {
      success = false
    }

    setCopiedState({ tokenId, success })

    copiedTimeoutRef.current = setTimeout(() => {
      setCopiedState(initialCopiedState)
    }, 3000)
  }

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current)
      }
    }
  }, [])

  const openTokenCreationDialog = () => {
    setCreateDialogState({ ...initialCreateDialogState, open: true })
  }

  const closeTokenCreationDialog = () => {
    if (createDialogState.requestInFlight) {
      return
    }
    setCreateDialogState(state => ({ ...state, open: false }))
  }

  const onTokenNameChange = event => {
    setCreateDialogState(state => ({
      ...state,
      tokenName: event.target.value,
      tokenNameError: null
    }))
  }

  const createToken = () => {
    setCreateDialogState(state => ({ ...state, serverError: null }))

    if (!createDialogState.tokenName) {
      setCreateDialogState(state => ({ ...state, tokenNameError: t.user.fieldRequired }))
      return
    }

    setCreateDialogState(state => ({ ...state, requestInFlight: true }))

    const submit = async () => {
      try {
        const { data } = await backend.post('/api/token/pat/', {
          name: createDialogState.tokenName,
          scopes: userData.patScopes.map(item => item.scope)
        })

        setTokenSecrets(state => ({ ...state, [data.id]: data.secret }))
        queryClient.invalidateQueries({ queryKey: ['userTokens'] })
        setCreateDialogState(state => ({ ...state, open: false, requestInFlight: false }))
      } catch (err) {
        const serverError =
          err?.response?.data?.detail ?? t.user.profile.createTokenDialogErrorUnknown
        setCreateDialogState(state => ({ ...state, serverError, requestInFlight: false }))
      }
    }
    submit()
  }

  const openTokenDeletionDialog = tokenId => () => {
    setDeleteDialogState({ ...initialDeleteDialogState, open: true, tokenId })
  }

  const closeTokenDeletionDialog = () => {
    if (deleteDialogState.requestInFlight) {
      return
    }
    setDeleteDialogState(state => ({ ...state, open: false }))
  }

  const deleteToken = () => {
    setDeleteDialogState(state => ({ ...state, requestInFlight: true }))

    const submit = async () => {
      try {
        await backend.post(`/api/token/pat/${deleteDialogState.tokenId}/revoke/`)
        queryClient.invalidateQueries({ queryKey: ['userTokens'] })
        setDeleteDialogState(state => ({ ...state, open: false, requestInFlight: false }))
      } catch (err) {
        const serverError =
          err?.response?.data?.detail ?? t.user.profile.createTokenDialogErrorUnknown
        setDeleteDialogState(state => ({ ...state, serverError, requestInFlight: false }))
      }
    }
    submit()
  }

  return (
    <Grid className="user-manage-tokens">
      <h3>{t.user.profile.manageTokensTitle}</h3>
      <p>{t.user.profile.manageTokensDescription}</p>
      {isLoading && <CircularProgress />}
      {!isLoading && (
        <>
          <div className="token-list">
            {activeTokens.map(token => (
              <div key={token.id} className="token-item">
                <div className="token-info">
                  <div className="token-name">{token.name}</div>
                  <div className="token-expiry">
                    {renderRichText(t.user.profile.tokenExpiresOn, {
                      date: getLongFormatDateTime(token.expiresAt)
                    })}
                  </div>
                  <div className="token-activity">
                    {renderRichText(t.user.profile.tokenLastUsed, { when: token.lastActivity })}
                  </div>
                </div>
                <Tooltip
                  title={t.user.profile.deleteToken}
                  placement="top"
                  arrow
                  disableInteractive
                >
                  <IconButton onClick={openTokenDeletionDialog(token.id)}>
                    <Delete />
                  </IconButton>
                </Tooltip>
                {Object.hasOwn(tokenSecrets, token.id) && (
                  <div className="token-secret-container">
                    <div className="token-secret-info">
                      <InfoOutline fontSize="small" />
                      {t.user.profile.tokenSecretInfo}
                    </div>
                    <div className="token-secret">
                      <span>{getTokenSecret(token.id)}</span>
                      <IconButton size="small" onClick={copyTokenSecret(token.id)}>
                        {copiedState.tokenId === token.id ? (
                          copiedState.success ? (
                            <CheckCircle fontSize="small" />
                          ) : (
                            <Cancel fontSize="small" />
                          )
                        ) : (
                          <ContentCopy fontSize="small" />
                        )}
                      </IconButton>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {activeTokens.length === 0 && (
              <div className="token-list-empty">{t.user.profile.noActiveTokens}</div>
            )}
          </div>
          <div className="token-create-row">
            <Button
              className="token-create"
              variant="contained"
              onClick={openTokenCreationDialog}
              disabled={activeTokens.length >= MAX_ACTIVE_TOKENS}
            >
              {t.user.profile.createToken}
            </Button>
            <div>
              <span className="token-active-count">
                <b>{activeTokens.length}</b> / {MAX_ACTIVE_TOKENS}
              </span>
              {t.user.profile.activeTokens}
            </div>
          </div>
        </>
      )}
      <Dialog
        className="user-manage-tokens-dialog"
        open={createDialogState.open}
        onClose={closeTokenCreationDialog}
      >
        <DialogTitle>{t.user.profile.createTokenDialogTitle}</DialogTitle>
        <DialogContent>
          <TextField
            name="name"
            label={t.user.profile.createTokenDialogFormFieldName}
            value={createDialogState.tokenName}
            onChange={onTokenNameChange}
            disabled={createDialogState.requestInFlight}
            error={!!createDialogState.tokenNameError}
            helperText={createDialogState.tokenNameError}
          />
          {createDialogState.serverError && (
            <FormHelperText error>{createDialogState.serverError}</FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={createToken}
            disabled={createDialogState.requestInFlight}
          >
            {t.user.profile.createTokenDialogActionCreate}
          </Button>
          <Button onClick={closeTokenCreationDialog} disabled={createDialogState.requestInFlight}>
            {t.user.profile.createTokenDialogActionCancel}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        className="user-manage-tokens-dialog"
        open={deleteDialogState.open}
        onClose={closeTokenDeletionDialog}
      >
        <DialogTitle>{t.user.profile.deleteTokenDialogTitle}</DialogTitle>
        <DialogContent>
          {deleteDialogState.tokenId && (
            <>
              <p>{t.user.profile.deleteTokenDialogTextDescription}</p>
              <p className="token-name">
                {tokens.find(token => token.id === deleteDialogState.tokenId)?.name}
              </p>
              <p>{t.user.profile.deleteTokenDialogTextConfirmation}</p>
            </>
          )}
          {deleteDialogState.serverError && (
            <FormHelperText error>{deleteDialogState.serverError}</FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={deleteToken}
            disabled={deleteDialogState.requestInFlight}
          >
            {t.user.profile.deleteTokenDialogActionDelete}
          </Button>
          <Button onClick={closeTokenDeletionDialog} disabled={deleteDialogState.requestInFlight}>
            {t.user.profile.deleteTokenDialogActionCancel}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

UserManageTokens.isEnabled = ({ patScopes }) => patScopes && patScopes.length > 0
UserManageTokens.tabPath = 'tokens'
UserManageTokens.getTabName = t => t.user.profile.manageTokensTitle

export default UserManageTokens
